import { useEffect, useState } from 'react';
import { apiClient } from '@/api/client';

interface Row {
  productId: string;
  productName: string;
  category: string;
  quantityOnHand: number;
}

/**
 * The bake log: what came out of the oven today. Logging a bake adds the
 * quantity to that branch's stock, which is how oversold products get back
 * to positive.
 */
export default function ProductionPanel({
  branchId,
  branchName,
}: {
  branchId: string;
  branchName?: string;
}) {
  const [rows, setRows] = useState<Row[]>([]);
  const [history, setHistory] = useState<any[]>([]);
  const [productId, setProductId] = useState('');
  const [quantity, setQuantity] = useState('');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(true);

  const load = async () => {
    if (!branchId) return;
    try {
      const [inv, hist] = await Promise.all([
        apiClient.getInventory(branchId),
        apiClient.getProductionHistory(branchId).catch(() => ({ data: [] })),
      ]);
      setRows(Array.isArray(inv.data) ? inv.data : []);
      setHistory(Array.isArray(hist.data) ? hist.data : []);
    } catch (e: any) {
      setErr(e?.response?.data?.message || 'Could not load products');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [branchId]);

  const submit = async () => {
    const qty = parseInt(quantity);
    if (!productId) {
      setErr('Pick a product.');
      return;
    }
    if (!qty || qty <= 0) {
      setErr('Enter how many were produced.');
      return;
    }
    setBusy(true);
    setErr('');
    try {
      await apiClient.logProduction(branchId, productId, qty, notes.trim() || undefined);
      const name = rows.find((r) => r.productId === productId)?.productName || 'product';
      setMsg(`Logged ${qty} × ${name}. Stock updated.`);
      setQuantity('');
      setNotes('');
      await load();
    } catch (e: any) {
      const d = e?.response?.data?.message;
      setErr(Array.isArray(d) ? d.join(', ') : d || 'Could not log production');
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <div className="text-gray-500 py-8">Loading…</div>;

  const selected = rows.find((r) => r.productId === productId);
  const qtyNum = parseInt(quantity) || 0;
  const todayTotal = history
    .filter((h: any) => new Date(h.createdAt).toDateString() === new Date().toDateString())
    .reduce((s: number, h: any) => s + (h.quantityProduced || 0), 0);

  return (
    <div className="space-y-6">
      {msg && <div className="bg-green-50 text-green-800 rounded-lg px-4 py-3 text-sm font-medium">{msg}</div>}
      {err && <div className="bg-red-50 text-red-700 rounded-lg px-4 py-3 text-sm font-medium">{err}</div>}

      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-lg shadow p-5">
          <h3 className="text-gray-500 text-xs font-semibold uppercase tracking-wide">Baked today</h3>
          <p className="mt-1 text-3xl font-bold text-gray-900 tabular-nums">{todayTotal}</p>
          <p className="text-xs text-gray-500 mt-1">units across all products</p>
        </div>
        <div className="bg-white rounded-lg shadow p-5">
          <h3 className="text-gray-500 text-xs font-semibold uppercase tracking-wide">Bakes logged</h3>
          <p className="mt-1 text-3xl font-bold text-gray-900 tabular-nums">{history.length}</p>
        </div>
        <div className="bg-white rounded-lg shadow p-5">
          <h3 className="text-gray-500 text-xs font-semibold uppercase tracking-wide">Oversold products</h3>
          <p className="mt-1 text-3xl font-bold text-red-600 tabular-nums">
            {rows.filter((r) => r.quantityOnHand < 0).length}
          </p>
          <p className="text-xs text-gray-500 mt-1">logging a bake fixes these</p>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b">
          <h3 className="text-lg font-medium text-gray-900">Log a bake</h3>
          <p className="text-sm text-gray-500">{branchName}</p>
        </div>
        <div className="p-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Product</label>
            <select
              value={productId}
              onChange={(e) => setProductId(e.target.value)}
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-amber-500 focus:border-transparent"
            >
              <option value="">Choose a product…</option>
              {rows.map((r) => (
                <option key={r.productId} value={r.productId}>
                  {r.productName} ({r.quantityOnHand} on hand)
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Quantity produced</label>
            <input
              type="number"
              min={1}
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
              placeholder="0"
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg tabular-nums text-lg font-semibold focus:ring-2 focus:ring-amber-500 focus:border-transparent"
            />
            {selected && qtyNum > 0 && (
              <p className="text-sm text-gray-600 mt-1.5 tabular-nums">
                {selected.quantityOnHand} → <span className="font-semibold text-green-700">{selected.quantityOnHand + qtyNum}</span> on hand
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Note (optional)</label>
            <input
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="e.g. morning bake"
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-500 focus:border-transparent"
            />
          </div>

          <div className="sm:col-span-2">
            <button
              onClick={submit}
              disabled={busy}
              className="w-full sm:w-auto bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-white font-bold px-6 py-2.5 rounded-lg"
            >
              {busy ? 'Logging…' : 'Log bake'}
            </button>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow">
        <h3 className="text-lg font-medium text-gray-900 px-6 py-4 border-b">Bake log</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b">
                <th className="text-left py-2.5 px-6 text-xs font-semibold uppercase tracking-wide text-gray-600">When</th>
                <th className="text-left py-2.5 px-4 text-xs font-semibold uppercase tracking-wide text-gray-600">Product</th>
                <th className="text-left py-2.5 px-4 text-xs font-semibold uppercase tracking-wide text-gray-600">By</th>
                <th className="text-right py-2.5 px-6 text-xs font-semibold uppercase tracking-wide text-gray-600">Qty</th>
              </tr>
            </thead>
            <tbody>
              {history.map((h: any, i: number) => (
                <tr key={h.id || i} className="border-b last:border-0">
                  <td className="py-2.5 px-6 text-gray-600">
                    {h.createdAt ? new Date(h.createdAt).toLocaleString('en-ZA', { dateStyle: 'short', timeStyle: 'short' }) : '—'}
                  </td>
                  <td className="py-2.5 px-4 font-medium text-gray-900">
                    {h.product?.name || h.recipe?.product?.name || '—'}
                  </td>
                  <td className="py-2.5 px-4 text-gray-600">{h.staff?.name || '—'}</td>
                  <td className="py-2.5 px-6 text-right tabular-nums font-semibold text-green-700">
                    +{h.quantityProduced}
                  </td>
                </tr>
              ))}
              {history.length === 0 && (
                <tr>
                  <td colSpan={4} className="py-10 text-center text-gray-500">
                    No bakes logged yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
